import { chatHistorySampleData } from '../constants/chatHistory'
import { authenticatedFetch, getCurrentUserUPN, isAuthenticated } from '../utils/authApi'

import { ChatMessage, Conversation, ConversationRequest, CosmosDBHealth, CosmosDBStatus, UserInfo } from './models'

// Bootstrap API - consolidated startup data
export interface BootstrapData {
  auth_config: {
    auth_mode: string;
    allow_public_chat: boolean;
    requires_admin_login: boolean;
  };
  models: {
    models: any[];
    default_model: string;
    model_options: Array<{id: string, label: string}>;
  };
  frontend_settings: any;
  authenticated: boolean;
  cosmos_db: {
    cosmosDB: boolean;
    status: string;
  };
  conversations: Conversation[];
  continuation_token: string | null;  // For pagination
  has_more_conversations: boolean;    // Whether more conversations exist
  admin_status: {
    is_admin: boolean;
    user_id: string | null;
  };
}

export const getBootstrapData = async (): Promise<BootstrapData | null> => {
  try {
    const response = await authenticatedFetch('/api/bootstrap/startup', {
      method: 'GET'
    });
    
    if (response.ok) {
      return await response.json();
    } else {
      console.error('Failed to load bootstrap data');
      return null;
    }
  } catch (error) {
    console.error('Error loading bootstrap data:', error);
    return null;
  }
};

export async function getUserInfo(): Promise<UserInfo[]> {
  try {
    // Use frontend auth helpers instead of hitting backend /auth/login
    const authed = await isAuthenticated();
    const upn = await getCurrentUserUPN();

    if (!authed || !upn) {
      // Not authenticated (in Azure AD mode) or no active account
      return [];
    }

    // Return a minimal UserInfo structure expected by the UI
    return [{
      access_token: '', // Not required for our fetch wrapper usage here
      expires_on: '',
      id_token: '',
      provider_name: 'azure_ad',
      user_claims: [
        { typ: 'preferred_username', val: upn },
      ],
      user_id: upn,
    }];
  } catch (error) {
    console.error('[AUTH] Error getting user info:', error);
    return [];
  }
}

// export const fetchChatHistoryInit = async (): Promise<Conversation[] | null> => {
export const fetchChatHistoryInit = (): Conversation[] | null => {
  // Make initial API call here

  return chatHistorySampleData
}

export const historyList = async (options?: { 
  continuationToken?: string | null, 
  pageSize?: number, 
  fetchAll?: boolean 
}): Promise<{ conversations: Conversation[] | null, continuationToken?: string | null, hasMore?: boolean }> => {
  // Build query params
  const params = new URLSearchParams()
  if (options?.continuationToken) {
    params.append('continuation_token', options.continuationToken)
  }
  if (options?.pageSize) {
    params.append('page_size', options.pageSize.toString())
  }
  if (options?.fetchAll) {
    params.append('all', 'true')
  }
  
  const queryString = params.toString()
  const url = queryString ? `/simple-chat/conversations?${queryString}` : '/simple-chat/conversations'
  
  // 🎯 UPDATED: Use simple chat conversations endpoint with pagination
  const response = await authenticatedFetch(url, {
    method: 'GET'
  })
    .then(async res => {
      const payload = await res.json()
      if (!payload.conversations || !Array.isArray(payload.conversations)) {
        console.error('There was an issue fetching your data.')
        return { conversations: null, continuationToken: null, hasMore: false }
      }
      
      // Process conversations from simple chat format
      const conversations: Conversation[] = payload.conversations.map((conv: any) => {
        const conversation: Conversation = {
          id: conv.id,
          fromBackend: true,
          title: conv.title || "New Conversation",
          date: conv.date || conv.last_updated || new Date().toISOString(),
          messages: [] // Load on demand
        }
        return conversation
      })
      return { 
        conversations, 
        continuationToken: payload.continuation_token || null,
        hasMore: payload.has_more || false
      }
    })
    .catch(_err => {
      console.error('There was an issue fetching your data.')
      return { conversations: null, continuationToken: null, hasMore: false }
    })

  return response
}

export const historyRead = async (convId: string): Promise<{ messages: ChatMessage[], pinnedWorkspaceId?: string | null }> => {
  // 🎯 UPDATED: Use simple chat history endpoint
  const response = await authenticatedFetch(`/simple-chat/history/${encodeURIComponent(convId)}`, {
    method: 'GET'
  })
    .then(async res => {
      if (!res.ok) {
        return { messages: [], pinnedWorkspaceId: null }
      }
      const payload = await res.json()
      const messages: ChatMessage[] = []
      if (payload?.messages) {
        payload.messages.forEach((msg: any) => {
          const message: ChatMessage = {
            id: msg.id || msg._id || Date.now().toString(),
            role: msg.role,
            date: msg.createdAt || msg.date || new Date().toISOString(),
            content: msg.content,
            feedback: msg.feedback ?? undefined
          }
          
          // Include metadata for charts/visuals if present
          if (msg.metadata || msg.chart_specs || msg.chart_data || msg.dashboard_data) {
            // Store as document_metadata for Answer component compatibility
            (message as any).document_metadata = [{
              chart_specs: msg.chart_specs || msg.metadata?.chart_specs,
              chart_data: msg.chart_data || msg.metadata?.chart_data,
              generated_dashboard: msg.dashboard_data || msg.metadata?.dashboard_data,
              table_data: msg.table_data || msg.metadata?.table_data,
              column_names: msg.column_names || msg.metadata?.column_names,
              total_rows: msg.total_rows || msg.metadata?.total_rows
            }]
            // Also set generated_dashboard directly on message for Answer component
            if (msg.dashboard_data || msg.metadata?.dashboard_data) {
              (message as any).generated_dashboard = msg.dashboard_data || msg.metadata?.dashboard_data
            }
          }
          
          // Include intent_reasoning if present
          if (msg.metadata?.intent_reasoning) {
            (message as any).intent_reasoning = msg.metadata.intent_reasoning
          }
          
          messages.push(message)
        })
      }
      return { messages, pinnedWorkspaceId: payload?.pinnedWorkspaceId || null }
    })
    .catch(_err => {
      console.error('There was an issue fetching your data.')
      return { messages: [], pinnedWorkspaceId: null }
    })
  return response
}

/**
 * Update the pinned workspace for a conversation
 */
export const updatePinnedWorkspace = async (convId: string, workspaceId: string | null): Promise<boolean> => {
  try {
    const response = await authenticatedFetch(`/history/${encodeURIComponent(convId)}/pinned-workspace`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ workspaceId })
    })
    return response.ok
  } catch (err) {
    console.error('Failed to update pinned workspace:', err)
    return false
  }
}

/**
 * Chat with streaming - returns tokens as they arrive via callbacks
 * This is the main chat function - always uses streaming for better UX
 * Includes auto-reconnection with Last-Event-ID for network resilience
 */
export const historyGenerate = async (
  options: ConversationRequest,
  abortSignal: AbortSignal,
  convId?: string,
  documents?: any[],
  webSearchMode?: boolean,
  callbacks?: {
    onToken?: (content: string) => void;
    onMetadata?: (metadata: { conversation_id: string; model: string }) => void;
    onDone?: (data: { source: string; finish_reason?: string; citations?: any[]; intent_reasoning?: any; is_cached?: boolean; chart_data?: any; chart_specs?: any[]; dashboard_data?: any; available_sheets?: any[]; sheet_name?: string; workspace_id?: string; show_document_panel?: boolean; action?: string; generated_document?: { document_url?: string; document_id?: string; filename?: string; format?: string; content_preview?: string; workspace_id?: string; title?: string; section_count?: number; action?: string } }) => void;
    onIntentReasoning?: (reasoning: { data_source: string; reason: string; confidence: number; thinking: string }) => void;
    onResearchPlan?: (plan: { queries: string[]; total_queries: number; estimated_sources: number; estimated_time_seconds: number }) => void;
    onClarification?: (clarification: { type: string; prompt: string; prompt_he: string; options: any[]; allow_custom?: boolean; original_query?: string }) => void;
    onWorkspaceRedirect?: (redirect: { workspace_id: string; workspace_title: string; mode: string; message?: string }) => void;
    onError?: (error: string) => void;
  }
): Promise<{ conversation_id: string | null; fullContent: string }> => {
  // Build request body
  const bodyObj: any = { messages: options.messages };
  if ((options as any).model) bodyObj.model = (options as any).model;
  if (convId) bodyObj.conversation_id = convId;
  if (documents?.length) bodyObj.documents = documents;
  if (webSearchMode) bodyObj.web_search_only = true;
  if ((options as any).selected_data_source) bodyObj.selected_data_source = (options as any).selected_data_source;
  if ((options as any).use_dynamic_system_message !== undefined) bodyObj.use_dynamic_system_message = (options as any).use_dynamic_system_message;
  
  // DALL-E image generation options
  if ((options as any).dalle_size) bodyObj.dalle_size = (options as any).dalle_size;
  if ((options as any).dalle_style) bodyObj.dalle_style = (options as any).dalle_style;
  if ((options as any).dalle_quality) bodyObj.dalle_quality = (options as any).dalle_quality;
  
  // Pinned workspace for document editing context
  if ((options as any).pinned_workspace_id) bodyObj.pinned_workspace_id = (options as any).pinned_workspace_id;

  let conversation_id: string | null = convId || null;
  let fullContent = '';
  let streamCompleted = false; // Track if we received 'done' event
  let lastEventId = ''; // Track last event ID for reconnection
  let reconnectAttempts = 0;
  const maxReconnectAttempts = 3;
  const baseReconnectDelay = 1000; // 1 second

  // Helper to parse SSE events with event ID tracking
  const parseSSELine = (line: string): { eventId?: string; data?: string } => {
    if (line.startsWith('id: ')) {
      return { eventId: line.slice(4).trim() };
    } else if (line.startsWith('data: ')) {
      return { data: line.slice(6) };
    }
    return {};
  };

  const attemptStream = async (): Promise<void> => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Accept': 'text/event-stream'
    };
    
    // Send Last-Event-ID header on reconnection
    if (lastEventId) {
      headers['Last-Event-ID'] = lastEventId;
      console.log(`[SSE] Reconnecting with Last-Event-ID: ${lastEventId}`);
    }

    const response = await authenticatedFetch('/simple-chat/stream', {
      method: 'POST',
      headers,
      body: JSON.stringify(bodyObj),
      signal: abortSignal
    });

    if (!response.ok) {
      const errorText = await response.text();
      
      // Special handling for token limit exceeded (429)
      if (response.status === 429) {
        try {
          const errorData = JSON.parse(errorText);
          const blockedReason = errorData.message || errorData.blocked_reason || 'Token limit exceeded';
          const blockedBy = errorData.blocked_by || 'unknown';
          callbacks?.onError?.(`TOKEN_LIMIT_EXCEEDED:${blockedBy}:${blockedReason}`);
        } catch {
          callbacks?.onError?.('TOKEN_LIMIT_EXCEEDED:unknown:You have reached your token usage limit. Please contact your administrator.');
        }
        throw new Error('TOKEN_LIMIT');
      }
      
      throw new Error(errorText || `HTTP ${response.status}`);
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('No response body');
    }

    // Reset reconnect attempts on successful connection
    reconnectAttempts = 0;

    const decoder = new TextDecoder();
    let buffer = '';
    let currentEventId = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        // Skip empty lines and comment lines (heartbeats)
        if (!line || line.startsWith(':')) continue;
        
        const parsed = parseSSELine(line);
        
        // Track event ID
        if (parsed.eventId) {
          currentEventId = parsed.eventId;
          lastEventId = currentEventId;
        }
        
        // Process data
        if (parsed.data) {
          try {
            const chunk = JSON.parse(parsed.data);
            switch (chunk.type) {
              case 'metadata':
                conversation_id = chunk.conversation_id;
                callbacks?.onMetadata?.({ conversation_id: chunk.conversation_id, model: chunk.model });
                break;
              case 'intent_reasoning':
                callbacks?.onIntentReasoning?.(chunk.intent_reasoning);
                break;
              case 'research_plan':
                callbacks?.onResearchPlan?.(chunk.plan);
                break;
              case 'clarification':
                callbacks?.onClarification?.(chunk.clarification);
                break;
              case 'token':
                fullContent += chunk.content;
                callbacks?.onToken?.(chunk.content);
                break;
              case 'keepalive':
                // Keepalive events are sent during long operations (like Office365 processing)
                // to prevent browser timeout. Just ignore them silently.
                break;
              case 'workspace_redirect':
                // Redirect user to a different workspace (e.g., TenderBuilder for document fill)
                callbacks?.onWorkspaceRedirect?.({
                  workspace_id: chunk.workspace_id,
                  workspace_title: chunk.workspace_title,
                  mode: chunk.mode,
                  message: chunk.message
                });
                break;
              case 'done':
                streamCompleted = true; // Mark stream as successfully completed
                callbacks?.onDone?.({ source: chunk.source, finish_reason: chunk.finish_reason, citations: chunk.citations, intent_reasoning: chunk.intent_reasoning, chart_data: chunk.chart_data, dashboard_data: chunk.dashboard_data, generated_document: chunk.generated_document, action: chunk.action, workspace_id: chunk.workspace_id });
                break;
              case 'error':
                callbacks?.onError?.(chunk.content);
                break;
            }
          } catch { /* skip malformed chunks */ }
        }
      }
    }
  };

  // Main loop with reconnection
  while (!streamCompleted && reconnectAttempts < maxReconnectAttempts) {
    try {
      await attemptStream();
      break; // Clean exit
    } catch (error: any) {
      if (error.name === 'AbortError' || error.message === 'TOKEN_LIMIT') {
        // Don't retry on user abort or token limit
        if (error.message !== 'TOKEN_LIMIT' && !streamCompleted) {
          // User aborted - not an error
        }
        break;
      }

      if (streamCompleted) {
        // Stream completed successfully before error
        break;
      }

      reconnectAttempts++;
      
      if (reconnectAttempts < maxReconnectAttempts) {
        // Calculate delay with exponential backoff
        const delay = baseReconnectDelay * Math.pow(2, reconnectAttempts - 1);
        console.log(`[SSE] Connection lost, attempting reconnect ${reconnectAttempts}/${maxReconnectAttempts} in ${delay}ms`);
        await new Promise(resolve => setTimeout(resolve, delay));
      } else {
        // All retries exhausted
        console.error('[Chat] Streaming error after retries:', error);
        callbacks?.onError?.(error.message || 'Request failed');
      }
    }
  }

  return { conversation_id, fullContent };
}



export const historyUpdate = async (messages: ChatMessage[], convId: string): Promise<Response> => {
  const response = await authenticatedFetch('/history/update', {
    method: 'POST',
    body: JSON.stringify({
      conversation_id: convId,
      messages: messages
    }),
    headers: {
      'Content-Type': 'application/json'
    }
  })
    .then(async res => {
      return res
    })
    .catch(_err => {
      console.error('There was an issue fetching your data.')
      const errRes: Response = {
        ...new Response(),
        ok: false,
        status: 500
      }
      return errRes
    })
  return response
}

export const historyDelete = async (convId: string): Promise<Response> => {
  const response = await authenticatedFetch(`/direct-history/conversations/${convId}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json'
    }
  })
    .then(res => {
      return res
    })
    .catch(_err => {
      console.error('There was an issue fetching your data.')
      const errRes: Response = {
        ...new Response(),
        ok: false,
        status: 500
      }
      return errRes
    })
  return response
}

export const historyDeleteAll = async (): Promise<Response> => {
  const response = await authenticatedFetch('/history/delete_all', {
    method: 'DELETE',
    body: JSON.stringify({}),
    headers: {
      'Content-Type': 'application/json'
    }
  })
    .then(res => {
      return res
    })
    .catch(_err => {
      console.error('There was an issue fetching your data.')
      const errRes: Response = {
        ...new Response(),
        ok: false,
        status: 500
      }
      return errRes
    })
  return response
}

export const historyClear = async (convId: string): Promise<Response> => {
  const response = await authenticatedFetch('/history/clear', {
    method: 'POST',
    body: JSON.stringify({
      conversation_id: convId
    }),
    headers: {
      'Content-Type': 'application/json'
    }
  })
    .then(res => {
      return res
    })
    .catch(_err => {
      console.error('There was an issue fetching your data.')
      const errRes: Response = {
        ...new Response(),
        ok: false,
        status: 500
      }
      return errRes
    })
  return response
}

export const historyRename = async (convId: string, title: string): Promise<Response> => {
  const response = await authenticatedFetch('/history/rename', {
    method: 'POST',
    body: JSON.stringify({
      conversation_id: convId,
      title: title
    }),
    headers: {
      'Content-Type': 'application/json'
    }
  })
    .then(res => {
      return res
    })
    .catch(_err => {
      console.error('There was an issue fetching your data.')
      const errRes: Response = {
        ...new Response(),
        ok: false,
        status: 500
      }
      return errRes
    })
  return response
}

export const historyGetDocuments = async (convId: string): Promise<any[]> => {
  if (!convId) {
    console.error('Cannot fetch documents: No conversation ID provided');
    return [];
  }

  try {
    const response = await authenticatedFetch(`/simple-chat/${encodeURIComponent(convId)}/documents`, {
      method: 'GET'
    });
    
    if (response.ok) {
      const data = await response.json();
      return data.documents || [];
    } else {
      console.error('Failed to fetch conversation documents');
      return [];
    }
  } catch (error) {
    console.error('Error fetching conversation documents:', error);
    return [];
  }
};

export const historyAddDocument = async (conversationId: string, document: any): Promise<boolean> => {
  if (!conversationId || !document) {
    console.error('Cannot add document: Missing conversation ID or document data');
    return false;
  }

  try {
    const response = await authenticatedFetch(`/simple-chat/${encodeURIComponent(conversationId)}/documents`, {
      method: 'POST',
      body: JSON.stringify(document),
      headers: {
        'Content-Type': 'application/json'
      }
    });
    
    return response.ok;
  } catch (error) {
    console.error('Error adding document to conversation:', error);
    return false;
  }
};

export const historyRemoveDocument = async (conversationId: string, documentId: string): Promise<Response> => {
  const response = await authenticatedFetch('/history/documents/remove', {
    method: 'DELETE',
    body: JSON.stringify({
      conversation_id: conversationId,
      document_id: documentId
    }),
    headers: {
      'Content-Type': 'application/json'
    }
  })
    .then(res => {
      return res
    })
    .catch(_err => {
      console.error('There was an issue removing the document from conversation.')
      const errRes: Response = {
        ...new Response(),
        ok: false,
        status: 500
      }
      return errRes
    })
  return response
}

export const historyEnsure = async (): Promise<CosmosDBHealth> => {
  const response = await authenticatedFetch('/history/ensure', {
    method: 'GET'
  })
    .then(async res => {
      const respJson = await res.json()
      let formattedResponse
      if (respJson.cosmosDB === true || respJson.status === "CosmosDB is configured and working") {
        formattedResponse = CosmosDBStatus.Working
      } else {
        if (res.status === 500) {
          formattedResponse = CosmosDBStatus.NotWorking
        } else if (res.status === 401) {
          formattedResponse = CosmosDBStatus.InvalidCredentials
        } else if (res.status === 422) {
          formattedResponse = respJson.error
        } else {
          formattedResponse = CosmosDBStatus.NotConfigured
        }
      }
      if (!res.ok) {
        return {
          cosmosDB: false,
          status: formattedResponse
        }
      } else {
        return {
          cosmosDB: true,
          status: formattedResponse
        }
      }
    })
    .catch(err => {
      console.error('There was an issue fetching your data.')
      return {
        cosmosDB: false,
        status: err
      }
    })
  return response
}

// Image generation API (Imagen)
export type ImageGenerateResponse = {
  model: string
  prompt: string
  images: { b64: string; mime_type: string }[]
} | { error: string }

export async function generateImage(prompt: string, opts?: { n?: number; size?: string; model?: string }): Promise<ImageGenerateResponse> {
  try {
    const body: any = { prompt };
    if (opts?.n) body.n = opts.n;
    if (opts?.size) body.size = opts.size;
    if (opts?.model) body.model = opts.model;
    const response = await authenticatedFetch('/image/generate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    const data = await response.json();
    return data as ImageGenerateResponse;
  } catch (e: any) {
    return { error: e?.message || 'Unknown error' };
  }
}

export const frontendSettings = async (): Promise<Response | null> => {
  const response = await authenticatedFetch('/frontend_settings', {
    method: 'GET'
  })
    .then(res => {
      return res.json()
    })
    .catch(_err => {
      console.error('There was an issue fetching your data.')
      return null
    })

  return response
}
export const historyMessageFeedback = async (messageId: string, feedback: string): Promise<Response> => {
  const response = await authenticatedFetch('/history/message_feedback', {
    method: 'POST',
    body: JSON.stringify({
      message_id: messageId,
      message_feedback: feedback
    }),
    headers: {
      'Content-Type': 'application/json'
    }
  })
    .then(res => {
      return res
    })
    .catch(_err => {
      console.error('There was an issue logging feedback.')
      const errRes: Response = {
        ...new Response(),
        ok: false,
        status: 500
      }
      return errRes
    })
  return response
}
